import * as THREE from 'three';
import { HUMANOID_BONES, type HumanoidBone, type HumanoidRig } from './humanoidRigTypes';
// packages/character-render/src/humanoidRig.ts
//
// Procedural poses for the rig: the per-bone pose function and the bone-matrix
// pass the animator calls every frame. Split out of humanoidRig.ts (which
// re-exports everything here).

// ---------------------------------------------------------------------------
// Poses
// ---------------------------------------------------------------------------

/** Built-in procedural animations. `none` keeps the rest pose (no per-frame work). */
export type HumanoidAnimation = 'none' | 'idle' | 'walk' | 'attack' | 'death';

/** Rotation (radians, XYZ) + vertical lift (fraction of figure height) for one bone. */
export interface BonePose {
  rx: number;
  ry: number;
  rz: number;
  lift: number;
}

export function emptyPose(): BonePose {
  return { rx: 0, ry: 0, rz: 0, lift: 0 };
}

/**
 * Procedural pose for one bone at time `t` (seconds). Pure and allocation
 * free: writes into `out`. `phase` de-synchronises a crowd of NPCs.
 *
 * - idle: breathing bob (body lifts ~1.2% of height), a slow head turn,
 *   arms settle slightly away from the body.
 * - walk: opposite arm/leg swing and a step bob.
 * - attack: right arm chops forward on a 0.6 s loop.
 * - death: the whole figure tips over sideways within 0.6 s and stays down.
 */
export function poseHumanoidBone(
  animation: HumanoidAnimation,
  bone: HumanoidBone,
  t: number,
  phase: number,
  out: BonePose,
): BonePose {
  out.rx = 0;
  out.ry = 0;
  out.rz = 0;
  out.lift = 0;
  if (animation === 'none') return out;

  if (animation === 'idle') {
    const breath = Math.sin(t * 2.1 + phase);
    switch (bone) {
      case 'body':
        out.lift = 0.006 + breath * 0.006;
        out.rx = breath * 0.012;
        break;
      case 'head':
        // Two incommensurate sines: the gaze drifts instead of ticking back and forth.
        out.ry = Math.sin(t * 0.37 + phase * 1.7) * 0.42 + Math.sin(t * 0.91 + phase) * 0.08;
        out.rx = Math.sin(t * 0.53 + phase) * 0.05 - breath * 0.015;
        break;
      case 'left_arm':
        out.rz = -0.05 - breath * 0.02;
        out.rx = Math.sin(t * 0.8 + phase) * 0.04;
        break;
      case 'right_arm':
        out.rz = 0.05 + breath * 0.02;
        out.rx = Math.sin(t * 0.8 + phase + 1.3) * 0.04;
        break;
      default:
        break;
    }
    return out;
  }

  if (animation === 'walk') {
    const s = Math.sin(t * 6 + phase);
    switch (bone) {
      case 'body':
        out.lift = Math.abs(Math.cos(t * 6 + phase)) * 0.015;
        break;
      case 'head':
        out.ry = s * 0.05;
        break;
      case 'left_arm':
        out.rx = -s * 0.5;
        break;
      case 'right_arm':
        out.rx = s * 0.5;
        break;
      case 'left_leg':
        out.rx = s * 0.55;
        break;
      case 'right_leg':
        out.rx = -s * 0.55;
        break;
      default:
        break;
    }
    return out;
  }

  if (animation === 'attack') {
    const k = ((t + phase) % 0.6) / 0.6;
    const chop = Math.sin(k * Math.PI);
    if (bone === 'right_arm') out.rx = -1.6 * chop;
    if (bone === 'body') out.ry = -0.25 * chop;
    return out;
  }

  // death
  if (bone === 'root') out.rz = Math.min(1, t / 0.6) * (Math.PI / 2) * 0.95;
  return out;
}

let poseScratch: {
  boneLocal: THREE.Matrix4;
  t1: THREE.Matrix4;
  t2: THREE.Matrix4;
  q: THREE.Quaternion;
  e: THREE.Euler;
} | null = null;
function getPoseScratch() {
  poseScratch ??= {
    boneLocal: new THREE.Matrix4(),
    t1: new THREE.Matrix4(),
    t2: new THREE.Matrix4(),
    q: new THREE.Quaternion(),
    e: new THREE.Euler(),
  };
  return poseScratch;
}

function pivotRotation(
  out: THREE.Matrix4,
  pivot: THREE.Vector3,
  pose: BonePose,
  liftY: number,
): THREE.Matrix4 {
  const { t1: _t1, t2: _t2, q: _q, e: _e } = getPoseScratch();
  _e.set(pose.rx, pose.ry, pose.rz);
  _q.setFromEuler(_e);
  // T(pivot + lift) · R · T(−pivot)
  _t1.makeRotationFromQuaternion(_q);
  _t2.makeTranslation(-pivot.x, -pivot.y, -pivot.z);
  out.multiplyMatrices(_t1, _t2);
  _t1.makeTranslation(pivot.x, pivot.y + liftY, pivot.z);
  return out.premultiply(_t1);
}

/**
 * Compute every bone's world-in-figure matrix for the current pose. Head and
 * arms inherit the body (so they bob with the breath); legs hang off the root.
 * `out` must hold HUMANOID_BONES.length matrices.
 */
export function computeBoneMatrices(
  rig: Pick<HumanoidRig, 'pivots' | 'height'>,
  animation: HumanoidAnimation,
  t: number,
  phase: number,
  poseScratch: BonePose,
  out: THREE.Matrix4[],
): void {
  const h = rig.height || 1;
  const _boneLocal = getPoseScratch().boneLocal;
  // root
  poseHumanoidBone(animation, 'root', t, phase, poseScratch);
  pivotRotation(out[0]!, rig.pivots[0]!, poseScratch, 0);
  // body = root · local
  poseHumanoidBone(animation, 'body', t, phase, poseScratch);
  pivotRotation(_boneLocal, rig.pivots[1]!, poseScratch, poseScratch.lift * h);
  out[1]!.multiplyMatrices(out[0]!, _boneLocal);
  // head + arms = body · local
  for (let i = 2; i <= 4; i++) {
    poseHumanoidBone(animation, HUMANOID_BONES[i]!, t, phase, poseScratch);
    pivotRotation(_boneLocal, rig.pivots[i]!, poseScratch, poseScratch.lift * h);
    out[i]!.multiplyMatrices(out[1]!, _boneLocal);
  }
  // legs = root · local
  for (let i = 5; i <= 6; i++) {
    poseHumanoidBone(animation, HUMANOID_BONES[i]!, t, phase, poseScratch);
    pivotRotation(_boneLocal, rig.pivots[i]!, poseScratch, poseScratch.lift * h);
    out[i]!.multiplyMatrices(out[0]!, _boneLocal);
  }
}
